import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthViewModel } from '../viewmodels/AuthViewModel';
import styled from 'styled-components';
import { colors, typography, spacing, borderRadius } from '../styles/designTokens';

// 로딩 컨테이너 스타일
const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: ${colors.secondary[50]};
`;

// 로딩 스피너 스타일
const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${colors.neutral[200]};
  border-top: 4px solid ${colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 로딩 텍스트 스타일
const LoadingText = styled.p`
  margin-top: ${spacing[4]};
  color: ${colors.secondary[500]};
  font-size: ${typography.fontSize.sm};
`;

const InlineErrorBox = styled.div`
  margin-top: ${spacing[4]};
  padding: ${spacing[3]};
  background-color: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  border-radius: ${borderRadius.md};
  color: ${colors.error[600]};
  font-size: ${typography.fontSize.sm};
  text-align: center;
`;

// 로딩 래퍼 스타일
const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// ProtectedRoute Props 인터페이스
interface ProtectedRouteProps {
  children: React.ReactNode;
}

// 보호된 라우트 컴포넌트
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const location = useLocation();
  const { isLoggedIn, isLoading, error, initializeAuth, clearError } = useAuthViewModel();
  const hasInitialized = useRef(false);

  // 컴포넌트 마운트 시 인증 상태 초기화 (한 번만 실행)
  useEffect(() => {
    if (!hasInitialized.current) {
      console.log('ProtectedRoute - 인증 상태 초기화 시작');
      hasInitialized.current = true;
      initializeAuth();
    }
  }, [initializeAuth]);

  // 로그인 상태 변경 감지 로그
  useEffect(() => {
    console.log('ProtectedRoute - 로그인 상태 변경:', { isLoggedIn, isLoading, error });
  }, [isLoggedIn, isLoading, error]);

  // 에러 메시지가 있으면 잠시 표시 후 클리어
  useEffect(() => {
    if (error) {
      console.log('ProtectedRoute - 에러 메시지 표시:', error);
      const timer = setTimeout(() => {
        clearError();
      }, 5000); // 5초 후 에러 메시지 클리어

      return () => clearTimeout(timer);
    }
  }, [error, clearError]);

  // 로딩 중일 때 로딩 스피너 표시
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingWrapper>
          <LoadingSpinner />
          <LoadingText>인증 상태를 확인하고 있습니다...</LoadingText>
          {error && <InlineErrorBox>{error}</InlineErrorBox>}
        </LoadingWrapper>
      </LoadingContainer>
    );
  }

  // 로그인되지 않은 경우 로그인 페이지로 리다이렉트
  if (!isLoggedIn) {
    console.log('ProtectedRoute - 로그인되지 않음, 로그인 페이지로 리다이렉트');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // 인증된 경우 자식 컴포넌트 렌더링
  console.log('ProtectedRoute - 인증됨, 자식 컴포넌트 렌더링');
  return <>{children}</>;
};

export default ProtectedRoute; 